import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

/**
 * Combines clsx and tailwind-merge for optimal class name handling
 * - clsx: Handles conditional classes and various input formats
 * - twMerge: Intelligently merges Tailwind classes, resolving conflicts
 * 
 * @example
 * cn('px-4 py-2', 'p-3') // => 'p-3' (p-3 overrides px-4 py-2)
 * cn('text-red-500', isError && 'text-blue-500') // => conditional classes
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Formats a string to be URL-friendly (slug)
 */
export function slugify(str: string): string {
  return str
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * Debounce function for performance optimization
 */
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout | null = null;
  
  return function executedFunction(...args: Parameters<T>) {
    const later = () => {
      timeout = null;
      func(...args);
    };
    
    if (timeout !== null) {
      clearTimeout(timeout);
    }
    
    timeout = setTimeout(later, wait);
  };
}

/**
 * Throttle function for rate limiting
 */
export function throttle<T extends (...args: any[]) => any>(
  func: T,
  limit: number
): (...args: Parameters<T>) => void {
  let inThrottle: boolean = false;
  
  return function executedFunction(...args: Parameters<T>) {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
}

/**
 * Check if code is running in browser
 */
export const isBrowser = typeof window !== 'undefined';

/**
 * Check if code is running on server
 */
export const isServer = !isBrowser;

/**
 * Safe localStorage access
 */
export const storage = {
  get: (key: string): string | null => {
    if (!isBrowser) return null;
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  
  set: (key: string, value: string): void => {
    if (!isBrowser) return;
    try {
      localStorage.setItem(key, value);
    } catch {
      // Fail silently
    }
  },
  
  remove: (key: string): void => {
    if (!isBrowser) return;
    try {
      localStorage.removeItem(key);
    } catch {
      // Fail silently
    }
  },
  
  clear: (): void => {
    if (!isBrowser) return;
    try {
      localStorage.clear();
    } catch {
      // Fail silently
    }
  },
};

/**
 * Format bytes to human readable string
 */
export function formatBytes(bytes: number, decimals = 2): string {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB'];
  
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
}

/**
 * Generate a random ID
 */
export function generateId(prefix?: string): string {
  const id = Math.random().toString(36).substring(2, 9);
  return prefix ? `${prefix}-${id}` : id;
}

/**
 * Check if a value is empty (null, undefined, empty string, empty array, empty object)
 */
export function isEmpty(value: any): boolean {
  if (value == null) return true;
  if (typeof value === 'string') return value.trim().length === 0;
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'object') return Object.keys(value).length === 0;
  return false;
}

/**
 * Deep merge objects
 */
export function deepMerge<T extends Record<string, any>>(
  target: T,
  ...sources: Partial<T>[]
): T {
  if (!sources.length) return target;
  const source = sources.shift();
  
  if (isObject(target) && isObject(source)) {
    for (const key in source) {
      if (isObject(source[key])) {
        if (!target[key]) Object.assign(target, { [key]: {} });
        deepMerge(target[key] as Record<string, any>, source[key] as Record<string, any>);
      } else {
        Object.assign(target, { [key]: source[key] });
      }
    }
  }
  
  return deepMerge(target, ...sources);
}

/**
 * Check if value is a plain object
 */
function isObject(item: any): item is Record<string, any> {
  return item && typeof item === 'object' && !Array.isArray(item);
}

/**
 * Capitalize first letter of string
 */
export function capitalize(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

/**
 * Get initials from name
 */
export function getInitials(name: string): string {
  return name
    .split(' ')
    .map(word => word[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

/**
 * Case-insensitive sort comparator for Table sort logic.
 *
 * Rules:
 * - Nullish values (null/undefined) always sort to the end
 * - Both numbers → numeric compare
 * - Both strings → locale-aware case-insensitive compare
 * - Mixed types → convert both to string and compare case-insensitively
 *
 * @returns negative if a < b, 0 if equal, positive if a > b
 *
 * @example
 * ['Abc','abc','ABD'].sort(caseInsensitiveSort) // ['abc','Abc','ABD']
 */
export function caseInsensitiveSort(a: unknown, b: unknown): number {
  // Nullish values sort to the end
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;

  // Numeric compare if both are numbers
  if (typeof a === 'number' && typeof b === 'number') {
    if (a === b) return 0;
    return a < b ? -1 : 1;
  }

  // String compare (case-insensitive)
  if (typeof a === 'string' && typeof b === 'string') {
    return a.toLowerCase().localeCompare(b.toLowerCase());
  }

  // Mixed: convert both to string for comparison
  return String(a).toLowerCase().localeCompare(String(b).toLowerCase());
}

/**
 * Temperature conversion utilities
 * All weather data is stored in Celsius, these functions convert for display
 */

/**
 * Convert Celsius to Fahrenheit
 * Formula: F = (C * 9/5) + 32
 */
export function celsiusToFahrenheit(celsius: number): number {
  return (celsius * 9 / 5) + 32;
}

/**
 * Convert Fahrenheit to Celsius
 * Formula: C = (F - 32) * 5/9
 */
export function fahrenheitToCelsius(fahrenheit: number): number {
  return (fahrenheit - 32) * 5 / 9;
}

/**
 * Convert temperature based on unit preference
 * @param celsius - Temperature in Celsius (stored value)
 * @param unit - Display unit preference ('celsius' | 'fahrenheit')
 * @returns Temperature in the requested unit
 */
export function convertTemperature(celsius: number | null, unit: 'celsius' | 'fahrenheit' | null | undefined): number | null {
  if (celsius === null || celsius === undefined) return null;
  if (unit === 'fahrenheit') {
    return celsiusToFahrenheit(celsius);
  }
  return celsius; // Default to Celsius
}

/**
 * Get temperature unit symbol
 * @param unit - Temperature unit preference
 * @returns Unit symbol ('°C' or '°F')
 */
export function getTemperatureUnitSymbol(unit: 'celsius' | 'fahrenheit' | null | undefined): string {
  return unit === 'fahrenheit' ? '°F' : '°C';
}

/**
 * Convert kilometers to miles
 * Formula: miles = km * 0.621371
 */
export function kmToMiles(km: number): number {
  return km * 0.621371;
}

/**
 * Convert distance based on unit preference
 * @param km - Distance in kilometers (stored value)
 * @param unit - Display unit preference ('kilometers' | 'miles')
 * @returns Distance in the requested unit
 */
export function convertDistance(km: number | null, unit: 'kilometers' | 'miles' | null | undefined): number | null {
  if (km === null || km === undefined) return null;
  if (unit === 'miles') {
    return kmToMiles(km);
  }
  return km; // Default to kilometers
}

/**
 * Get distance unit label
 * @param unit - Distance unit preference
 * @returns Unit label ('km' or 'mi')
 */
export function getDistanceUnitLabel(unit: 'kilometers' | 'miles' | null | undefined): string {
  return unit === 'miles' ? 'mi' : 'km';
}